import { useEffect, useRef, useState } from 'react'

/**
 * How long the screen stays up after the work is done.
 *
 * `.gl-progress-fill` animates its width over 350ms, so a screen torn down the instant the last
 * asset lands leaves the bar frozen part-way through that animation — the player sees it stop at
 * 50%, or at 0% when everything was already cached and the whole load resolved in one step. The
 * hold is that transition plus a beat to register "full".
 */
export const LOADING_COMPLETE_HOLD_MS = 520

export type LoadingProgressView = {
  /** 0-100, never decreasing, and always 100 by the time the screen goes away. */
  pct: number
  /** Denominator for the "%" suffix; 0 keeps the indeterminate "Loading…" label. */
  total: number
  /** While true the loading screen must stay mounted — includes the closing 100% beat. */
  visible: boolean
}

/**
 * Turns raw loader counts into something a player can watch.
 *
 * Two rules, both about not lying to the eye:
 *
 * 1. **Monotonic.** A loader that discovers more work (or a second pipeline reporting on the same
 *    channel) can push the fraction backwards. A progress bar that runs backwards reads as a bug
 *    even when the number is accurate.
 * 2. **Always finishes.** Real loads end at less than 100%: assets the CDN reports as gone, a
 *    partial-complete pass, a safety timeout, or simply a cache scan that resolves everything at
 *    once. None of those are worth showing as an unfinished bar — the wait IS over — so completion
 *    pins the bar to 100% and holds it there long enough to be seen.
 */
export function useLoadingProgressView(
  loaded: number,
  total: number,
  finished: boolean,
  /** Changing this starts a fresh bar — a new route area, or a new bootstrap phase. */
  resetKey: string | number = 'default',
): LoadingProgressView {
  const highestRef = useRef(0)
  const keyRef = useRef(resetKey)
  const [holdElapsed, setHoldElapsed] = useState(false)

  if (keyRef.current !== resetKey) {
    keyRef.current = resetKey
    highestRef.current = 0
  }

  const rawPct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0
  const target = finished ? 100 : rawPct
  if (target > highestRef.current) highestRef.current = target
  const pct = highestRef.current

  useEffect(() => {
    if (!finished) {
      setHoldElapsed(false)
      return
    }
    // Started only once `finished` flips, so the 100% frame is painted at the top of the hold.
    const id = window.setTimeout(() => setHoldElapsed(true), LOADING_COMPLETE_HOLD_MS)
    return () => window.clearTimeout(id)
  }, [finished])

  return { pct, total, visible: !finished || !holdElapsed }
}
